import { haversineDistance } from './distanceCalculator.js';

const IMPOSSIBLE_JUMP_MIN_DISTANCE_M = 1_000;
const MAX_PLAUSIBLE_GLIDER_SPEED_MPS = 120;
const SECOND_PILOT_NOISE_MAX_SPEED_MPS = 5;
const SECOND_PILOT_NOISE_MAX_AGL_M = 75;
const CONTINUITY_RESET_GAP_MS = 60_000;
const LIVE_LOG_THROTTLE_MS = 10_000;

function parseFiniteNumber(value) {
  if (value == null || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getLat(position) {
  return parseFiniteNumber(position?.lat ?? position?.latitude);
}

function getLon(position) {
  return parseFiniteNumber(position?.lon ?? position?.longitude);
}

function getTimestampMs(position) {
  const rawTimestamp = position?.timestamp;
  if (rawTimestamp instanceof Date) {
    return rawTimestamp.getTime();
  }
  if (rawTimestamp == null) {
    return null;
  }
  const parsed = new Date(rawTimestamp).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function getSpeedMps(position) {
  return parseFiniteNumber(position?.speed_mps);
}

function getAltitudeAglM(position) {
  return parseFiniteNumber(position?.altitude_agl_m);
}

function getContinuityLabel(position, continuityKey) {
  return (
    position?.id_cn ||
    position?.callsign ||
    continuityKey ||
    position?.cookie ||
    'unknown'
  );
}

function looksGrounded(position) {
  const speedMps = getSpeedMps(position);
  if (speedMps == null || speedMps > SECOND_PILOT_NOISE_MAX_SPEED_MPS) {
    return false;
  }

  const altitudeAglM = getAltitudeAglM(position);
  return altitudeAglM == null || altitudeAglM <= SECOND_PILOT_NOISE_MAX_AGL_M;
}

function getImpossibleJumpDecision(previousAccepted, candidate) {
  const prevLat = getLat(previousAccepted);
  const prevLon = getLon(previousAccepted);
  const candidateLat = getLat(candidate);
  const candidateLon = getLon(candidate);

  if (
    !Number.isFinite(prevLat) ||
    !Number.isFinite(prevLon) ||
    !Number.isFinite(candidateLat) ||
    !Number.isFinite(candidateLon)
  ) {
    return null;
  }

  const jumpMeters = haversineDistance(prevLat, prevLon, candidateLat, candidateLon);
  if (!(jumpMeters >= IMPOSSIBLE_JUMP_MIN_DISTANCE_M)) {
    return null;
  }

  const previousTsMs = getTimestampMs(previousAccepted);
  const candidateTsMs = getTimestampMs(candidate);
  const timeGapMs = (
    previousTsMs != null && candidateTsMs != null
      ? candidateTsMs - previousTsMs
      : null
  );

  if (timeGapMs == null || timeGapMs <= 0) {
    return null;
  }

  const impliedSpeedMps = jumpMeters / (timeGapMs / 1000);
  if (!(impliedSpeedMps > MAX_PLAUSIBLE_GLIDER_SPEED_MPS)) {
    return null;
  }

  return {
    jumpMeters,
    timeGapMs,
    impliedSpeedMps,
    candidateGrounded: looksGrounded(candidate),
    previousGrounded: looksGrounded(previousAccepted)
  };
}

export class PositionNoiseFilter {
  constructor() {
    this.liveStateByKey = new Map();
  }

  clear() {
    this.liveStateByKey.clear();
  }

  acceptLivePosition(position, continuityKey = position?.cookie, source = 'live') {
    if (!position || !continuityKey) {
      return true;
    }

    const state = this.liveStateByKey.get(continuityKey) || {
      lastAccepted: null,
      droppedCount: 0,
      lastLogAtMs: 0
    };
    this.liveStateByKey.set(continuityKey, state);

    if (!state.lastAccepted) {
      state.lastAccepted = position;
      return true;
    }

    const previousTsMs = getTimestampMs(state.lastAccepted);
    const candidateTsMs = getTimestampMs(position);
    if (
      previousTsMs != null &&
      candidateTsMs != null &&
      candidateTsMs - previousTsMs > CONTINUITY_RESET_GAP_MS
    ) {
      state.lastAccepted = position;
      return true;
    }

    const jumpDecision = getImpossibleJumpDecision(state.lastAccepted, position);
    if (!jumpDecision) {
      state.lastAccepted = position;
      return true;
    }

    // If the previous anchor looks parked/grounded and the new point does not,
    // treat the older anchor as noise and move forward with the new track.
    if (jumpDecision.previousGrounded && !jumpDecision.candidateGrounded) {
      state.lastAccepted = position;
      return true;
    }

    // If both sides look grounded, prefer the newer one so stale parked anchors
    // don't block a new session from being accepted.
    if (jumpDecision.previousGrounded && jumpDecision.candidateGrounded) {
      state.lastAccepted = position;
      return true;
    }

    state.droppedCount += 1;
    const nowMs = Date.now();
    if (nowMs - state.lastLogAtMs >= LIVE_LOG_THROTTLE_MS) {
      const jumpKm = (jumpDecision.jumpMeters / 1000).toFixed(1);
      const gapSec = (jumpDecision.timeGapMs / 1000).toFixed(1);
      const impliedKmh = (jumpDecision.impliedSpeedMps * 3.6).toFixed(0);
      console.warn(
        `[NoiseFilter] Dropped impossible jump for ${getContinuityLabel(position, continuityKey)} `
        + `(source=${source}, jump=${jumpKm}km, gap=${gapSec}s, implied=${impliedKmh}km/h, dropped=${state.droppedCount})`
      );
      state.lastLogAtMs = nowMs;
    }
    return false;
  }

  filterSequence(positions, options = {}) {
    if (!Array.isArray(positions) || positions.length === 0) {
      return { positions: [], removed: 0 };
    }

    const {
      continuityKey = positions[0]?.cookie || null
    } = options;

    let lastAccepted = null;
    const filtered = [];
    let removed = 0;

    positions.forEach((position) => {
      if (!lastAccepted) {
        lastAccepted = position;
        filtered.push(position);
        return;
      }

      const previousTsMs = getTimestampMs(lastAccepted);
      const candidateTsMs = getTimestampMs(position);
      if (
        previousTsMs != null &&
        candidateTsMs != null &&
        candidateTsMs - previousTsMs > CONTINUITY_RESET_GAP_MS
      ) {
        lastAccepted = position;
        filtered.push(position);
        return;
      }

      const jumpDecision = getImpossibleJumpDecision(lastAccepted, position);
      if (!jumpDecision) {
        lastAccepted = position;
        filtered.push(position);
        return;
      }

      if (jumpDecision.previousGrounded) {
        while (filtered.length > 0 && looksGrounded(filtered[filtered.length - 1])) {
          filtered.pop();
          removed += 1;
        }
        lastAccepted = filtered.length > 0 ? filtered[filtered.length - 1] : null;
        if (!jumpDecision.candidateGrounded) {
          filtered.push(position);
          lastAccepted = position;
          return;
        }
        if (!lastAccepted) {
          filtered.push(position);
          lastAccepted = position;
          return;
        }
      }

      if (jumpDecision.candidateGrounded) {
        removed += 1;
        return;
      }

      lastAccepted = position;
      filtered.push(position);
    });

    return {
      positions: filtered,
      removed,
      continuityKey
    };
  }
}

export const positionNoiseFilter = new PositionNoiseFilter();
